/**
 * Bundle 46 Task 5 - Build info panel for super admins.
 *
 * Renders as a card at the bottom of AdminDataHealthPage. Shows the
 * env / bundle / git sha / built_at / boot_at that the running
 * backend reports. Includes a manual refresh button so admins can
 * confirm a fresh deploy landed without opening a new tab.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Copy, RefreshCw, Server } from 'lucide-react';
import api from '@/utils/api';

const fmtRel = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    const diff = Date.now() - d.getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  } catch (e) { return null; }
};

const BuildInfoCard = () => {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/build-info');
      setInfo(data);
    } catch (e) {
      toast.error('Could not load build info.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const copySha = () => {
    if (!info?.git_sha_full) return;
    try {
      navigator.clipboard.writeText(info.git_sha_full);
      toast.success('Git SHA copied.');
    } catch (e) {
      toast.error('Clipboard blocked.');
    }
  };

  if (!info) {
    return (
      <Card data-testid="admin-build-info-card">
        <CardContent className="p-5 text-sm text-neutral-500">
          {loading ? 'Loading build info...' : 'Build info unavailable.'}
        </CardContent>
      </Card>
    );
  }

  const envColor = info.env_mismatch
    ? 'bg-red-100 text-red-800 ring-red-200'
    : info.env === 'production'
      ? 'bg-emerald-100 text-emerald-800 ring-emerald-200'
      : info.env === 'preview'
        ? 'bg-blue-100 text-blue-800 ring-blue-200'
        : 'bg-neutral-100 text-neutral-700 ring-neutral-200';

  const highest = (info.bundles_shipped || []).slice(-1)[0];

  return (
    <Card data-testid="admin-build-info-card">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-display text-lg font-semibold inline-flex items-center gap-2">
              <Server className="h-4 w-4 text-neutral-500" />
              Live build info
            </h3>
            <p className="mt-1 text-xs text-neutral-500">
              What&apos;s actually running on this server right now. Refresh
              after a deploy to confirm the new bundle landed.
            </p>
          </div>
          <Button
            size="sm" variant="outline"
            onClick={load} disabled={loading}
            data-testid="admin-build-info-refresh"
          >
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
          <div>
            <div className="text-[10px] uppercase tracking-wide text-neutral-500 font-semibold">
              Environment
            </div>
            <div className="mt-1 flex items-center gap-2">
              <span
                data-testid="admin-build-info-env"
                className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-semibold ring-4 ${envColor}`}
              >
                {info.env}
              </span>
              <span className="text-xs text-neutral-500">
                from {info.env_source}
              </span>
            </div>
            {info.env_mismatch ? (
              <div className="mt-2 text-xs text-red-700">
                Mismatch! request_host says <code>{info.request_host}</code>{' '}
                but APP_ENV says <code>{info.app_env || 'unset'}</code>.
              </div>
            ) : null}
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wide text-neutral-500 font-semibold">
              Bundle
            </div>
            <div className="mt-1 font-display text-2xl font-bold" data-testid="admin-build-info-bundle">
              Bundle {highest ?? '?'}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wide text-neutral-500 font-semibold">
              Git SHA
            </div>
            <div className="mt-1 flex items-center gap-2">
              <code className="text-xs bg-neutral-100 px-1.5 py-0.5 rounded" data-testid="admin-build-info-git">
                {info.git_sha_short}
              </code>
              <button
                onClick={copySha}
                className="text-neutral-400 hover:text-neutral-900"
                title="Copy full SHA"
                data-testid="admin-build-info-copy-sha"
              >
                <Copy className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wide text-neutral-500 font-semibold">
              Built / Booted
            </div>
            <div className="mt-1 text-xs text-neutral-700" title={info.built_at}>
              Built {fmtRel(info.built_at)}
            </div>
            <div className="text-xs text-neutral-500" title={info.boot_at}>
              Booted {fmtRel(info.boot_at)}
            </div>
          </div>
        </div>
        <div className="mt-4">
          <div className="text-[10px] uppercase tracking-wide text-neutral-500 font-semibold mb-2">
            Bundles shipped
          </div>
          <div className="flex flex-wrap gap-1" data-testid="admin-build-info-bundles-list">
            {(info.bundles_shipped || []).map((n) => (
              <span
                key={n}
                className="inline-block bg-neutral-100 text-neutral-700 text-xs px-2 py-0.5 rounded"
              >
                {n}
              </span>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default BuildInfoCard;
